import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled, { createGlobalStyle, useTheme } from 'styled-components'
import moment from 'moment'
import { wipAiFixture, monthLabel, shortMonth } from './mocks/wipAiFixture'
import { useWipAiStore, LIVE_CANVAS_ID, WIP_AI_PENDING_REV_ID } from './store/useWipAiStore'
import { useWipReportAiData } from './lib/useWipReportAiData'
import WipAiTopBar from './Components/WipAiTopBar'
import WipAiReviewBanner from './Components/WipAiReviewBanner'
import WipAiReportHeader from './Components/WipAiReportHeader'
import WipAiActionPoints from './Components/WipAiActionPoints'
import WipAiKpiStrip from './Components/WipAiKpiStrip'
import WipAiPacingChart from './Components/WipAiPacingChart'
import WipAiFinancialSummaryTable from './Components/WipAiFinancialSummaryTable'
import WipAiStaffUtilizationTable from './Components/WipAiStaffUtilizationTable'
import WipAiExpensesBreakdown from './Components/WipAiExpensesBreakdown'
import WipAiNarrativeEditor from './Components/WipAiNarrativeEditor'
import WipAiKeyDatesTimeline from './Components/WipAiKeyDatesTimeline'
import WipAiExportFooter from './Components/WipAiExportFooter'
import WipAiPreviewModal from './Components/WipAiPreviewModal'
import WipAiToast from './Components/WipAiToast'
import WipAiSidebarShell from './Components/AiSidebar/WipAiSidebarShell'
import { I } from './Components/Icons'

const SIDEBAR_STORAGE_KEY = 'wipAi-sidebar-width'

/** Shown in the top bar until live WIP data or saved revisions exist */
const WIP_AI_PENDING_REV = {
  id: WIP_AI_PENDING_REV_ID,
  revisionNumber: '—',
  source: 'user',
  caption: '',
  timeLabel: 'Loading…',
  createdAt: '',
  snapshot: {
    oration: '',
    budgetTrend: 'On Target',
    wipData: null,
    keyDates: [],
    findings: [],
  },
}

const FlashStyle = createGlobalStyle`
  @keyframes wipAiAnchorFlash {
    0% {
      box-shadow: 0 0 0 0 rgba(66, 65, 191, 0.55);
    }
    60% {
      box-shadow: 0 0 0 6px rgba(66, 65, 191, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(66, 65, 191, 0);
    }
  }
  #wip-ai-main .wip-ai-flash {
    animation: wipAiAnchorFlash 1.4s ease-out;
  }
`

const AppGrid = styled.div`
  display: grid;
  grid-template-columns: ${p =>
    p.$collapsed ? '1fr 56px' : `1fr minmax(360px, ${p.$sidebarW}px)`};
  min-height: 100vh;
  transition: grid-template-columns 0.2s ease-out;
  background: ${p => p.theme.colors.paper};
`

const Main = styled.main`
  min-width: 0;
  padding: 28px 36px 120px;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
`

const EmptyState = styled.div`
  margin-top: 48px;
  padding: 36px 28px;
  border-radius: 12px;
  border: 1px dashed ${p => p.theme.colors.semantic.border};
  background: ${p => p.theme.colors.semantic.surface};
  text-align: center;
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
`

const EmptyTitle = styled.h2`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 18px;
  margin: 0 0 10px;
  color: ${p => p.theme.colors.semantic.textPrimary};
`

const EmptyBody = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${p => p.theme.colors.semantic.textMuted};
`

const Tweaks = styled.div`
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 100;
  width: 240px;
  background: ${p => p.theme.colors.semantic.surface};
  border: 1px solid ${p => p.theme.colors.semantic.border};
  border-radius: 12px;
  box-shadow: ${p => p.theme.colors.semantic.shadowLg};
  padding: 14px;
  display: ${p => (p.$open ? 'block' : 'none')};
`

const TweaksH = styled.h4`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin: 0 0 10px;
  color: ${p => p.theme.colors.semantic.textMuted};
`

const WipReportAiView = ({
  client,
  projects = [],
  selectedProject,
  onProjectSelect,
  invoiceTotals: _invoiceTotals = [],
  person: _person,
  skipAiHydration = false,
}) => {
  void _invoiceTotals
  void _person
  const theme = useTheme()
  const revisions = useWipAiStore(s => s.revisions)
  const selectedRevId = useWipAiStore(s => s.selectedRevId)
  const conversation = useWipAiStore(s => s.conversation)
  const generating = useWipAiStore(s => s.generating)
  const toastMessage = useWipAiStore(s => s.toastMessage)
  const clearToastMessage = useWipAiStore(s => s.clearToastMessage)

  const selectRev = useWipAiStore(s => s.selectRev)
  const returnToLatest = useWipAiStore(s => s.returnToLatest)
  const restoreRevision = useWipAiStore(s => s.restoreRevision)
  const generateSummary = useWipAiStore(s => s.generateSummary)
  const sendMessage = useWipAiStore(s => s.sendMessage)
  const submitRevision = useWipAiStore(s => s.submitRevision)
  const discardProposal = useWipAiStore(s => s.discardProposal)
  const hydrateWipAi = useWipAiStore(s => s.hydrateWipAi)
  const aiHydrating = useWipAiStore(s => s.aiHydrating)

  const [selectedReportMonth, setSelectedReportMonth] = useState(null)

  const {
    today,
    monthlyWipReports,
    availableMonths,
    rpt: dataRpt,
    keyDates: dataKeyDates,
    weeklyPacing,
    pacingUsd,
    trend: hookTrend,
    forecast,
    historySparks,
    periodLabel,
    staffUtilization,
    isLoading,
  } = useWipReportAiData({ client, selectedProject, selectedMonth: selectedReportMonth })

  const prevProjectIdRef = useRef()
  useEffect(() => {
    if (!selectedProject) {
      prevProjectIdRef.current = undefined
      setSelectedReportMonth(null)
      return
    }
    const id = String(selectedProject._id)
    if (prevProjectIdRef.current !== id) {
      prevProjectIdRef.current = id
      setSelectedReportMonth(null)
    }
  }, [selectedProject])

  useEffect(() => {
    if (!selectedProject || !availableMonths.length) return
    setSelectedReportMonth(prev => {
      if (prev && availableMonths.includes(prev)) return prev
      const cal = moment().format('YYYY-MM')
      return availableMonths.includes(cal) ? cal : availableMonths[availableMonths.length - 1]
    })
  }, [selectedProject, availableMonths])

  useEffect(() => {
    if (skipAiHydration) return
    if (!client || !selectedProject?._id || !selectedReportMonth) return
    hydrateWipAi({
      client,
      projectId: String(selectedProject._id),
      month: selectedReportMonth,
    })
  }, [client, selectedProject, selectedReportMonth, hydrateWipAi, skipAiHydration])

  const [collapsed, setCollapsed] = useState(false)
  const [sidebarW, setSidebarW] = useState(() => {
    if (typeof window === 'undefined') return 440
    const v = parseInt(window.localStorage.getItem(SIDEBAR_STORAGE_KEY) || '440', 10)
    return Number.isFinite(v) && v >= 360 && v <= 640 ? v : 440
  })
  const [tweaksOpen, setTweaksOpen] = useState(false)
  const [showActionPoints, setShowActionPoints] = useState(true)
  const [toast, setToast] = useState(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [citeHighlight, setCiteHighlight] = useState(null)

  const resizingRef = useRef(false)

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarW))
  }, [sidebarW])

  useEffect(() => {
    if (!toastMessage) return
    setToast(toastMessage)
    clearToastMessage()
    const t = window.setTimeout(() => setToast(null), 2400)
    return () => window.clearTimeout(t)
  }, [toastMessage, clearToastMessage])

  useEffect(() => {
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        e.preventDefault()
        setCollapsed(c => !c)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const showToast = useCallback(text => {
    setToast(text)
    window.setTimeout(() => setToast(null), 2400)
  }, [])

  const onResizerDown = e => {
    resizingRef.current = true
    document.body.style.userSelect = 'none'
    const onMove = ev => {
      if (!resizingRef.current) return
      const w = window.innerWidth - ev.clientX
      setSidebarW(Math.max(360, Math.min(640, w)))
    }
    const onUp = () => {
      resizingRef.current = false
      document.body.style.userSelect = ''
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  const liveCanvasRev = useMemo(() => {
    if (!dataRpt || !selectedReportMonth) return null
    return {
      id: LIVE_CANVAS_ID,
      revisionNumber: 0,
      source: 'user',
      caption: 'Live data',
      timeLabel: 'Live',
      createdAt: '',
      snapshot: {
        oration: '',
        budgetTrend: hookTrend || 'On Target',
        wipData: dataRpt,
        keyDates: dataKeyDates || [],
        findings: [],
      },
    }
  }, [dataRpt, selectedReportMonth, dataKeyDates, hookTrend])

  const latestRev = revisions.length
    ? revisions[revisions.length - 1]
    : liveCanvasRev ?? WIP_AI_PENDING_REV
  const activeRev = useMemo(() => {
    const fromSel = revisions.find(r => r.id === selectedRevId)
    if (fromSel) return fromSel
    if (revisions.length) return revisions[revisions.length - 1]
    return liveCanvasRev ?? WIP_AI_PENDING_REV
  }, [revisions, selectedRevId, liveCanvasRev])

  const isViewingPast =
    !!activeRev && !!latestRev && activeRev.id !== latestRev.id && activeRev.id !== LIVE_CANVAS_ID

  const revisionsSidebar = revisions.map(r => ({
    id: r.id,
    revisionNumber: r.revisionNumber,
    source: r.source,
    caption: r.caption,
    timeLabel: r.timeLabel,
  }))

  const projectShape = useMemo(() => {
    if (!selectedProject) {
      return {
        _id: '',
        name: 'Select matter',
        client: '—',
        Address: '',
        CurrencyIsoCode: 'USD',
        matterNumber: '—',
      }
    }
    return {
      _id: selectedProject._id,
      salesforce_id: selectedProject.salesforce_id,
      name: selectedProject.name,
      client: selectedProject.client || '—',
      Address: selectedProject.Address,
      CurrencyIsoCode: selectedProject.CurrencyIsoCode || 'USD',
      matterNumber: selectedProject.projectCode || selectedProject.Matter_Name || '—',
    }
  }, [selectedProject])

  const rpt = selectedProject && dataRpt ? dataRpt : activeRev?.snapshot?.wipData
  const keyDatesForTimeline =
    selectedProject && dataKeyDates?.length ? dataKeyDates : activeRev?.snapshot?.keyDates || []

  const displayActiveRev = useMemo(() => {
    if (!activeRev) return null
    if (!selectedProject || !dataRpt) return activeRev
    return {
      ...activeRev,
      snapshot: {
        ...activeRev.snapshot,
        wipData: dataRpt,
        keyDates: dataKeyDates?.length ? dataKeyDates : activeRev.snapshot.keyDates,
        budgetTrend: hookTrend,
      },
    }
  }, [activeRev, selectedProject, dataRpt, dataKeyDates, hookTrend])

  const pacingChartTitle = selectedReportMonth
    ? `Weekly Pacing — ${moment(selectedReportMonth, 'YYYY-MM').format('MMMM YYYY')}`
    : 'Weekly Pacing'
  const monthLabelText = selectedReportMonth
    ? monthLabel(selectedReportMonth)
    : monthLabel(moment().format('YYYY-MM'))

  const onAnchor = anchor => {
    if (!anchor) return
    const el = document.getElementById(anchor)
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 80
      window.scrollTo({ top: y, behavior: 'smooth' })
      el.classList.add('wip-ai-flash')
      window.setTimeout(() => el.classList.remove('wip-ai-flash'), 1500)
    }
  }

  const handleReturnToLatest = () => {
    const ret = returnToLatest()
    if (ret.toast) showToast(ret.toast)
  }

  const handleRestore = async () => {
    const ret = await restoreRevision()
    if (ret?.toast) showToast(ret.toast)
  }

  const handleSubmitRevision = async id => {
    const ret = await submitRevision(id)
    if (ret?.toast) showToast(ret.toast)
  }

  const handleExportStub = () => {
    showToast('Export wired to backend in next iteration')
  }

  const highlightPerson = citeHighlight?.includes('Priya')
    ? 'Priya Shah'
    : citeHighlight?.includes('Jordan')
    ? 'Jordan Ellis'
    : null

  const showReportBody =
    !!selectedProject && !!selectedReportMonth && !!dataRpt && !isLoading && !!displayActiveRev
  const showLoadingBody =
    !!selectedProject &&
    (isLoading || aiHydrating || (availableMonths.length > 0 && !selectedReportMonth))
  const showNoDataBody =
    !!selectedProject && !isLoading && !aiHydrating && availableMonths.length === 0

  return (
    <>
      <FlashStyle />
      <AppGrid $collapsed={collapsed} $sidebarW={sidebarW}>
        <div style={{ gridColumn: '1 / -1' }}>
          <WipAiTopBar
            projectName={selectedProject?.name || 'Matters'}
            availableReportMonths={availableMonths}
            selectedReportMonth={selectedReportMonth}
            onSelectReportMonth={setSelectedReportMonth}
            monthSelectDisabled={!selectedProject || isLoading || !availableMonths.length}
            projects={projects}
            selectedProject={selectedProject}
            onSelectProject={onProjectSelect}
            activeRev={activeRev}
            latestRev={latestRev}
            onPreview={() => setPreviewOpen(true)}
            onExportDocx={handleExportStub}
            onTweaks={() => setTweaksOpen(t => !t)}
          />
        </div>

        <Main id="wip-ai-main">
          {!selectedProject && (
            <EmptyState>
              <EmptyTitle>Select a matter</EmptyTitle>
              <EmptyBody>
                Choose a project from the <b>Matter</b> control in the toolbar to load WIP data for
                this report.
              </EmptyBody>
            </EmptyState>
          )}

          {showLoadingBody && (
            <EmptyState>
              <EmptyTitle>Loading WIP…</EmptyTitle>
              <EmptyBody>Fetching report and key dates for {selectedProject.name}.</EmptyBody>
            </EmptyState>
          )}

          {showNoDataBody && (
            <EmptyState>
              <EmptyTitle>No WIP months returned</EmptyTitle>
              <EmptyBody>
                The WIP report for this matter has no monthly rows in the selected date range.
              </EmptyBody>
            </EmptyState>
          )}

          {showReportBody && rpt && (
            <>
              {isViewingPast && (
                <WipAiReviewBanner
                  activeRev={activeRev}
                  latestRev={latestRev}
                  onReturnToLatest={handleReturnToLatest}
                  onRestore={handleRestore}
                />
              )}

              <WipAiReportHeader
                project={projectShape}
                reportingPeriodLabel={periodLabel}
                isViewingPast={!!isViewingPast}
              />

              <WipAiActionPoints
                findings={displayActiveRev.snapshot.findings || []}
                onAnchor={onAnchor}
                show={showActionPoints}
              />

              <WipAiKpiStrip
                rpt={rpt}
                iso={projectShape.CurrencyIsoCode}
                forecast={forecast}
                trend={hookTrend}
                pacingUsd={pacingUsd}
                monthLabelText={monthLabelText}
              />

              <WipAiPacingChart
                weekly={weeklyPacing}
                history={historySparks}
                iso={projectShape.CurrencyIsoCode}
                title={pacingChartTitle}
              />

              <WipAiFinancialSummaryTable
                monthlyReports={monthlyWipReports}
                currentMonth={selectedReportMonth}
                iso={projectShape.CurrencyIsoCode}
              />

              <WipAiStaffUtilizationTable
                rows={staffUtilization}
                iso={projectShape.CurrencyIsoCode}
                highlightPerson={highlightPerson}
              />

              <WipAiExpensesBreakdown
                expensesDetails={rpt.expensesDetails || []}
                sparks={wipAiFixture.expenseSparks}
                iso={projectShape.CurrencyIsoCode}
              />

              <WipAiNarrativeEditor
                narrative={displayActiveRev.snapshot.oration}
                revLabel={
                  displayActiveRev.id === LIVE_CANVAS_ID
                    ? 'Draft'
                    : `Rev ${displayActiveRev.revisionNumber}`
                }
                lastEdited={String(displayActiveRev.timeLabel || '').toLowerCase()}
                onRewrite={generateSummary}
                readOnly={!!isViewingPast}
              />

              <WipAiKeyDatesTimeline keyDates={keyDatesForTimeline} today={today} />

              <WipAiExportFooter
                revNumber={displayActiveRev.revisionNumber}
                caption={displayActiveRev.caption}
                monthLabelText={monthLabel(selectedReportMonth)}
                onPreview={() => setPreviewOpen(true)}
                onExportStub={handleExportStub}
              />
            </>
          )}
        </Main>

        <WipAiSidebarShell
          collapsed={collapsed}
          onToggleCollapsed={() => setCollapsed(c => !c)}
          conversation={conversation}
          revisions={revisionsSidebar}
          selectedRevId={selectedRevId ?? LIVE_CANVAS_ID}
          onSelectRev={selectRev}
          onGenerateSummary={generateSummary}
          onSendMessage={sendMessage}
          onSubmitRevision={handleSubmitRevision}
          onDiscardProposal={id => discardProposal(id)}
          generating={generating || aiHydrating}
          onNewThread={() =>
            showToast(
              `New thread — ${shortMonth(
                selectedReportMonth || moment().format('YYYY-MM'),
              )} conversation already active`,
            )
          }
          onCiteHover={setCiteHighlight}
          onResizerMouseDown={onResizerDown}
          theme={theme}
        />
      </AppGrid>

      <Tweaks $open={tweaksOpen}>
        <button
          type="button"
          onClick={() => setTweaksOpen(false)}
          style={{
            position: 'absolute',
            right: 8,
            top: 8,
            width: 24,
            height: 24,
            border: 'none',
            background: 'transparent',
            color: '#676767',
            cursor: 'pointer',
          }}
          aria-label="Close tweaks"
        >
          <I.x width={14} height={14} />
        </button>
        <TweaksH>Tweaks</TweaksH>
        <label
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
            margin: '8px 0',
          }}
        >
          <span>Sidebar width</span>
          <input
            type="range"
            min={360}
            max={640}
            step={10}
            value={sidebarW}
            onChange={e => setSidebarW(parseInt(e.target.value, 10))}
          />
        </label>
        <label
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
            margin: '8px 0',
          }}
        >
          <span>Show action points</span>
          <input
            type="checkbox"
            checked={showActionPoints}
            onChange={e => setShowActionPoints(e.target.checked)}
          />
        </label>
        <label
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
            margin: '8px 0',
          }}
        >
          <span>Collapse sidebar</span>
          <input
            type="checkbox"
            checked={collapsed}
            onChange={e => setCollapsed(e.target.checked)}
          />
        </label>
      </Tweaks>

      <WipAiToast message={toast} show={!!toast} />

      {previewOpen && displayActiveRev && (
        <WipAiPreviewModal rev={displayActiveRev} onClose={() => setPreviewOpen(false)} />
      )}
    </>
  )
}

WipReportAiView.propTypes = {
  client: PropTypes.object.isRequired,
  projects: PropTypes.arrayOf(PropTypes.object),
  selectedProject: PropTypes.object,
  onProjectSelect: PropTypes.func.isRequired,
  invoiceTotals: PropTypes.arrayOf(PropTypes.object),
  person: PropTypes.object,
  skipAiHydration: PropTypes.bool,
}

export default WipReportAiView
